import { useState } from "react";
import type { ReactNode } from "react";
import type { AuthSession, UserRole } from "../../types/vtc";
import { resendEmailCode, verifyEmailCode } from "../../lib/auth";
import { errorText } from "../../lib/api";
import { toast } from "../../lib/toast";
import { cn } from "../../lib/format";
import { useT } from "../../i18n";
import Button from "../ui/Button";

interface EmailCodeFormProps {
  /** Which account the code belongs to (driver or rental account). */
  role: UserRole;
  email: string;
  /** Called with the logged-in session once the code is accepted. */
  onVerified: (session: AuthSession, message: string) => void;
  /** Extra button left of "Verify email" (e.g. Back). */
  secondaryAction?: ReactNode;
}

/** "Verify your email": the 6-digit code e-mailed after the driver or rental account signup. */
export default function EmailCodeForm({ role, email, onVerified, secondaryAction }: EmailCodeFormProps) {
  const t = useT();
  const f = t.auth.driverSignup;
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const verify = async () => {
    if (code.length !== 6) return setError(f.codeRequired);
    setVerifying(true);
    try {
      const { session, message } = await verifyEmailCode(role, email, code);
      onVerified(session, message);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      toast.success(await resendEmailCode(role, email));
      setCode("");
      setError("");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void verify();
      }}
    >
      <div className="grid gap-3.5 rounded-card border border-line-strong p-6">
        <div>
          <div className="text-[17px] font-medium">{f.verifyTitle}</div>
          <p className="m-0 mt-1 text-sm leading-normal text-muted">
            {f.verifyBefore}
            <b className="font-medium text-ink">{email}</b>
            {f.verifyAfter}
          </p>
        </div>
        <input
          value={code}
          onChange={(e) => {
            setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
            setError("");
          }}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="••••••"
          aria-label={f.codeLabel}
          aria-invalid={!!error}
          autoFocus
          className={cn(
            "h-13 w-full rounded border bg-surface px-4 text-center font-mono text-[22px] font-medium tracking-[0.4em] text-ink outline-none focus:border-brand",
            error ? "border-tone-red-fg" : "border-transparent",
          )}
        />
        {error && <p className="-mt-2 mb-0 text-xs text-tone-red-fg">{error}</p>}
        <div>
          <button
            type="button"
            onClick={() => void resend()}
            disabled={resending}
            className="cursor-pointer border-0 bg-transparent p-0 text-[13px] text-brand hover:text-brand-link disabled:opacity-60"
          >
            {resending ? f.resending : f.resend}
          </button>
        </div>
        <p className="m-0 border-t border-[#F0F0F0] pt-3 text-xs leading-normal text-muted">{role === "company" ? t.auth.companySignup.codeNextNote : f.nextNote}</p>
      </div>

      <div className="mt-7 flex gap-2.5">
        {secondaryAction}
        <Button type="submit" disabled={verifying} className="h-11 flex-1 cursor-pointer border-0">
          {verifying ? f.verifying : f.verify}
        </Button>
      </div>
    </form>
  );
}
